import { Link, NavLink } from 'react-router-dom';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/cn';
import { usePlatformSession } from './session';
import { PLATFORM_NAV } from './nav';

export function useVisiblePlatformNav() {
  const actor = usePlatformSession((s) => s.actor);
  if (!actor) return [];
  return PLATFORM_NAV.map((s) => ({ ...s, items: s.items.filter((i) => actor.permissions.includes(i.permission)) })).filter((s) => s.items.length > 0);
}

export function PlatformIdentity({ collapsed }: { collapsed?: boolean }) {
  return (
    <Link to="/super-admin" className="flex min-w-0 items-center gap-3" aria-label="Super Admin home">
      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-secondary text-white shadow-glow"><ShieldCheck className="size-5" /></span>
      {!collapsed && (
        <span className="min-w-0 leading-tight">
          <span className="block truncate text-sm font-extrabold">Super Admin</span>
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Platform console</span>
        </span>
      )}
    </Link>
  );
}

export function PlatformSidebarNav({ collapsed, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const sections = useVisiblePlatformNav();
  return (
    <nav aria-label="Platform" className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
      {sections.map((s) => (
        <div key={s.title}>
          {!collapsed ? <p className="mb-1.5 px-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{s.title}</p> : <div className="mx-3 mb-2 h-px bg-border" aria-hidden />}
          <ul className="space-y-0.5">
            {s.items.map((i) => (
              <li key={i.to}>
                <NavLink
                  to={i.to} end={i.end} onClick={onNavigate} title={collapsed ? i.label : undefined}
                  className={({ isActive }) => cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors',
                    collapsed && 'justify-center px-0',
                    isActive ? 'bg-secondary text-white shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <i.icon className="size-[18px] shrink-0" aria-hidden />
                  <span className={cn(collapsed && 'sr-only')}>{i.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function PlatformSidebar({ collapsed }: { collapsed: boolean }) {
  return (
    <aside className={cn('fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-border bg-card transition-[width] duration-200 lg:flex', collapsed ? 'w-[4.5rem]' : 'w-64')}>
      <div className={cn('flex h-16 items-center border-b border-border', collapsed ? 'justify-center px-2' : 'px-5')}><PlatformIdentity collapsed={collapsed} /></div>
      <PlatformSidebarNav collapsed={collapsed} />
      <div className="border-t border-border p-3">
        <Link to="/admin" className={cn('flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground', collapsed && 'justify-center px-0')} title="Open a merchant admin">
          <ExternalLink className="size-[18px]" /><span className={cn(collapsed && 'sr-only')}>Merchant admin</span>
        </Link>
      </div>
    </aside>
  );
}
